import { useEffect, useId, useRef, useState } from "react";
import type { CreateOutputInput, OutputLine } from "@dramapie/shared";

import { OUTPUT_ASPECT_OPTIONS, OUTPUT_KIND_OPTIONS } from "../../lib/outputs";

export type OutputDialogMode = "create" | "rename";

export interface OutputDialogProps {
  open: boolean;
  mode: OutputDialogMode;
  /** 重命名时的那条线；新建时为 null。 */
  output: OutputLine | null;
  busy?: boolean;
  /** 服务端错误（被拒 / 网络失败），显示在表单下方。 */
  error?: string | null;
  onClose: () => void;
  onCreate: (input: CreateOutputInput) => void;
  /** 编辑态提交同样的三件套（服务端 M8 起允许改 kind / aspect）。 */
  onUpdate: (input: CreateOutputInput) => void;
}

/**
 * 新建 / 重命名产出线的表单（契约 M8 §4 验收 2：输入名字 + 形态 + 画幅）。
 *
 * 新建与编辑共用一个表单（标题 + 形态 + 画幅）：
 *   · 新建 → `POST /outputs`；
 *   · 编辑 → `PATCH /outputs/:id`，三件套都能改（建错形态不用删了重建）。
 * 归档中的线是只读的，编辑入口在浮层里就被禁掉了（服务端还会再挡一次）。
 */
export function OutputDialog({
  open,
  mode,
  output,
  busy = false,
  error,
  onClose,
  onCreate,
  onUpdate,
}: OutputDialogProps) {
  const headingId = useId();
  const titleId = useId();
  const kindId = useId();
  const aspectId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState<CreateOutputInput["kind"]>("drama");
  const [aspect, setAspect] = useState<CreateOutputInput["aspect"]>("9:16");
  const [localError, setLocalError] = useState<string | null>(null);

  // 每次打开都重置表单：上一次输了一半的标题不能带到下一条线。
  useEffect(() => {
    if (!open) return;
    setLocalError(null);
    if (mode === "rename" && output) {
      setTitle(output.title);
      setKind(output.kind);
      setAspect(output.aspect);
    } else {
      setTitle("");
      setKind("drama");
      setAspect("9:16");
    }
    const timer = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [open, mode, output]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const heading = mode === "create" ? "新建产出线" : "重命名产出线";
  const trimmed = title.trim();
  const message = localError ?? error ?? null;

  function submit() {
    if (busy) return;
    if (trimmed.length === 0) {
      setLocalError("标题不能为空");
      return;
    }
    if (trimmed.length > 40) {
      setLocalError("标题最多 40 个字");
      return;
    }
    setLocalError(null);
    if (mode === "create") onCreate({ title: trimmed, kind, aspect });
    else onUpdate({ title: trimmed, kind, aspect });
  }

  return (
    <div className="dp-scrim" role="presentation" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className="dp-dialog dp-output-dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="dp-output-head">
          <div>
            <p className="dp-label">outputs · 产出线</p>
            <h2 id={headingId} className="dp-output-dialog-title">
              {heading}
            </h2>
          </div>
          <button type="button" className="dp-icon-btn" aria-label="关闭" onClick={onClose}>
            ×
          </button>
        </header>

        <form
          className="dp-output-form"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <label className="dp-output-field" htmlFor={titleId}>
            <span className="dp-label">标题</span>
            <input
              id={titleId}
              ref={inputRef}
              className="dp-input"
              value={title}
              maxLength={40}
              placeholder="例如：动画版 / 广播剧"
              onChange={(event) => setTitle(event.target.value)}
            />
          </label>

          <label className="dp-output-field" htmlFor={kindId}>
            <span className="dp-label">形态</span>
            <select
              id={kindId}
              className="dp-input"
              value={kind}
              onChange={(event) => setKind(event.target.value as CreateOutputInput["kind"])}
            >
              {OUTPUT_KIND_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="dp-output-field" htmlFor={aspectId}>
            <span className="dp-label">画幅</span>
            <select
              id={aspectId}
              className="dp-input"
              value={aspect}
              onChange={(event) => setAspect(event.target.value as CreateOutputInput["aspect"])}
            >
              {OUTPUT_ASPECT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>

          <p className="dp-tiny-note">
            画幅决定生成与导出比例（模型只出 9:16 / 16:9）。
            {mode === "create"
              ? "新建产出线不会自动建集：建好后在左栏「集」里加。"
              : "改形态 / 画幅不影响已有的集与镜头。"}
          </p>

          {message ? (
            <p className="dp-output-error" role="alert">
              {message}
            </p>
          ) : null}

          <div className="dp-output-actions">
            <button type="button" className="dp-btn" onClick={onClose} disabled={busy}>
              取消
            </button>
            <button type="submit" className="dp-btn primary" disabled={busy}>
              {mode === "create" ? "创建" : "保存"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
